import { useEffect, useRef, useState } from 'react';

const DISMISS_DISTANCE = 96;
const DIRECTION_SLOP = 8;

// Own only a single downward pull that begins at the transcript's top edge.
export default function usePreviewPullDismiss(containerRef, { enabled, onDismiss }) {
    const [distance, setDistance] = useState(0);
    const dismissRef = useRef(onDismiss);
    const enabledRef = useRef(enabled);
    dismissRef.current = onDismiss;
    enabledRef.current = enabled;

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled) return undefined;
        let gesture = null;
        let ignoredClick = null;
        const selectingText = () => window.getSelection()?.isCollapsed === false;
        const cancel = () => {
            gesture = null;
            setDistance(0);
        };
        const begin = (kind, id, point, target) => {
            if (!enabledRef.current) { cancel(); return; }
            if (kind === 'pointer' && gesture?.kind === 'touch') return;
            const control = target.closest?.('button, a, input, textarea, select, [role="button"], [role="slider"], [contenteditable="true"]');
            if (container.scrollTop > 1 || selectingText() ||
                (control && !control.hasAttribute('data-transcript-message'))) return;
            gesture = { kind, id, x: point.clientX, y: point.clientY, target: control || target, claimed: false, distance: 0 };
        };
        const move = (kind, id, point, event) => {
            if (!enabledRef.current) { cancel(); return; }
            if (!gesture || gesture.kind !== kind || gesture.id !== id) return;
            const dx = point.clientX - gesture.x;
            const dy = point.clientY - gesture.y;
            if (selectingText() || container.scrollTop > 1 || dy < -DIRECTION_SLOP ||
                (Math.abs(dx) > DIRECTION_SLOP && Math.abs(dx) >= Math.abs(dy))) {
                cancel();
                return;
            }
            if (dy < DIRECTION_SLOP) {
                gesture.distance = Math.max(0, dy);
                if (gesture.claimed) setDistance(gesture.distance);
                return;
            }
            // Touch scrolling is intercepted only after downward intent is clear.
            if (event.type === 'touchmove' && !event.cancelable) {
                cancel();
                return;
            }
            gesture.claimed = true;
            gesture.distance = dy;
            if (event.cancelable) event.preventDefault();
            setDistance(Math.min(dy, DISMISS_DISTANCE * 1.5));
        };
        const finish = (kind, id, point, event) => {
            if (!enabledRef.current) { cancel(); return; }
            move(kind, id, point, event);
            if (!gesture || gesture.kind !== kind || gesture.id !== id) return;
            const completed = gesture;
            if (completed.claimed) ignoredClick = { target: completed.target, until: Date.now() + 400 };
            cancel();
            if (enabledRef.current && completed.claimed && completed.distance >= DISMISS_DISTANCE) dismissRef.current?.();
        };
        const touchStart = event => {
            if (event.touches.length !== 1) { cancel(); return; }
            const touch = event.touches[0];
            begin('touch', touch.identifier, touch, event.target);
        };
        const touchMove = event => {
            if (event.touches.length !== 1) { cancel(); return; }
            const touch = event.touches[0];
            move('touch', touch.identifier, touch, event);
        };
        const touchEnd = event => {
            const touch = event.changedTouches[0];
            if (touch) finish('touch', touch.identifier, touch, event);
        };
        const pointerStart = event => {
            if (event.isPrimary === false) { cancel(); return; }
            if (['touch', 'pen'].includes(event.pointerType)) begin('pointer', event.pointerId, event, event.target);
        };
        const pointerMove = event => move('pointer', event.pointerId, event, event);
        const pointerEnd = event => finish('pointer', event.pointerId, event, event);
        const pointerCancel = () => { if (gesture?.kind === 'pointer') cancel(); };
        const scroll = () => { if (container.scrollTop > 1) cancel(); };
        const selectionChange = () => { if (selectingText()) cancel(); };
        const click = event => {
            if (ignoredClick && Date.now() <= ignoredClick.until && ignoredClick.target.contains(event.target)) {
                ignoredClick = null;
                event.preventDefault();
                event.stopPropagation();
            }
        };
        const listeners = [
            ['touchstart', touchStart, { passive: true, capture: true }],
            ['touchmove', touchMove, { passive: false, capture: true }],
            ['touchend', touchEnd, true], ['touchcancel', cancel, true],
            ['pointerdown', pointerStart, true], ['pointermove', pointerMove, true],
            ['pointerup', pointerEnd, true], ['pointercancel', pointerCancel, true],
            ['scroll', scroll, { passive: true }], ['contextmenu', cancel, true], ['click', click, true],
        ];
        listeners.forEach(([name, handler, options]) => container.addEventListener(name, handler, options));
        document.addEventListener('selectionchange', selectionChange);
        return () => {
            listeners.forEach(([name, handler, options]) => container.removeEventListener(name, handler, options));
            document.removeEventListener('selectionchange', selectionChange);
            cancel();
        };
    }, [containerRef, enabled]);

    return { distance: enabled ? distance : 0, ready: enabled && distance >= DISMISS_DISTANCE };
}
